import { z } from 'zod';
import type { WizardData } from './types';

/**
 * Email validation schema using Zod
 */
const emailSchema = z.string().email();

/**
 * Validate email address
 */
export function isValidEmail(email: string): boolean {
  const result = emailSchema.safeParse(email);
  return result.success;
}

/**
 * Normalize subdomain: lowercase, only alphanumeric and hyphens,
 * no double hyphens, no leading/trailing hyphens
 */
export function normalizeSubdomain(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Check if subdomain is available (mock implementation)
 * TODO: Replace with actual API call
 */
export function checkSubdomainAvailability(subdomain: string): boolean {
  const taken = ['test', 'shop', 'admin', 'www', 'api'];
  return subdomain.length >= 2 && !taken.includes(subdomain.toLowerCase());
}

/**
 * Validate step data - returns true if the step is complete and valid
 */
export function isStepValid(stepIndex: number, data: WizardData): boolean {
  switch (stepIndex) {
    case 0: // Customer
      return !!(
        data.customerEmail.trim() &&
        isValidEmail(data.customerEmail) &&
        data.customerName.trim()
      );
    case 1: {
      // Shop
      const normalized = normalizeSubdomain(data.subdomain);
      const isAvailable = checkSubdomainAvailability(normalized);
      return !!(data.shopName.trim() && normalized.length >= 2 && isAvailable);
    }
    case 2: // Collection
      return !!data.collectionId;
    case 3: {
      // Design
      const hasFront = data.frontDesign && data.frontPlacement;
      const hasBack = data.backDesign && data.backPlacement;
      return !!(hasFront || hasBack);
    }
    case 4: // Review
      return true;
    default:
      return false;
  }
}

/**
 * Get the highest valid step index (for navigation guard)
 */
export function getHighestValidStep(data: WizardData): number {
  for (let i = 0; i < 5; i++) {
    if (!isStepValid(i, data)) {
      return i;
    }
  }
  return 4; // All steps valid
}

/**
 * Check if user can access a specific step
 */
export function canAccessStep(targetStep: number, data: WizardData): boolean {
  // Can always access step 0
  if (targetStep === 0) return true;

  // Can access if all previous steps are valid
  for (let i = 0; i < targetStep; i++) {
    if (!isStepValid(i, data)) {
      return false;
    }
  }
  return true;
}
